import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductionOrderFormView } from './ProductionOrderFormView';
import { useAuthStore } from '@/stores/auth';
import { resolveProfile } from '@/lib/profileAccess';
import { api } from '@/utils/api';

async function loadChoices(path:string, key:string) {
  const all:any[]=[];
  for(let page=1;;page++) {
    const response=await api.get(`${path}${path.includes('?')?'&':'?'}limit=100&page=${page}`);
    const payload=response.data.data;
    const rows=Array.isArray(payload)?payload:payload?.[key];
    if(!Array.isArray(rows))throw new Error('Invalid production selector response');
    all.push(...rows);
    if(page >= (response.data.pagination?.totalPages ?? 1))return {data:{data:all}};
  }
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface Product {
  id: string;
  name: string;
  sku?: string;
}

export interface BOM {
  id: string;
  name: string;
  productId: string;
  version?: number;
  _count?: { components: number };
}

export interface SalesOrder {
  id: string;
  number: string;
  status: string;
}

export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email: string;
}

export type Priority = 'low' | 'normal' | 'high' | 'urgent';

export interface FormData {
  productId: string;
  bomId: string;
  targetQty: string;
  priority: Priority;
  plannedStart: string;
  plannedEnd: string;
  salesOrderId: string;
  assignedTo: string;
  notes: string;
}

/* ------------------------------------------------------------------ */
/*  Priority config                                                    */
/* ------------------------------------------------------------------ */

export const PRIORITIES: { value: Priority; label: string; color: string; dot: string }[] = [
  { value: 'low',    label: 'Low',    color: 'text-gray-400',   dot: 'bg-gray-400' },
  { value: 'normal', label: 'Normal', color: 'text-blue-400',   dot: 'bg-blue-400' },
  { value: 'high',   label: 'High',   color: 'text-amber-400',  dot: 'bg-amber-400' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-400',    dot: 'bg-red-400' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/** SUN-238: an order being changed before production starts. The form is the create form, pre-filled. */
export interface EditOrder {
  id: string; number: string; productId: string; productName?: string; bomId: string; targetQty: number; priority: Priority;
  plannedStart: string | null; plannedEnd: string | null; notes: string | null; productLocked: boolean;
}
const dateOnly = (v: string | null) => (v ? v.slice(0, 10) : '');

export function ProductionOrderCreatePage({onCreated,onCancel,editOrder}: {onCreated?:()=>void;onCancel?:()=>void;editOrder?:EditOrder} = {}) {
  const isEdit=!!editOrder;
  const {user}=useAuthStore();
  const profileManager=resolveProfile(user)==='P07';
  const [loadError,setLoadError]=useState(false),[revision,setRevision]=useState(0);
  const navigate = useNavigate();

  /* ---- Data lists ---- */
  const [products, setProducts] = useState<Product[]>([]);
  const [bomMap, setBomMap] = useState<Record<string, BOM>>({});  // productId → BOM (1:1)
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  /* ---- UI state ---- */
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* ---- Form ---- */
  const initial: FormData = editOrder ? {
    productId: editOrder.productId, bomId: editOrder.bomId, targetQty: String(editOrder.targetQty), priority: editOrder.priority,
    plannedStart: dateOnly(editOrder.plannedStart), plannedEnd: dateOnly(editOrder.plannedEnd), salesOrderId: '', assignedTo: '', notes: editOrder.notes ?? '',
  } : {
    productId: '',
    bomId: '',
    targetQty: '',
    priority: 'normal',
    plannedStart: '',
    plannedEnd: '',
    salesOrderId: '',
    assignedTo: '',
    notes: '',
  };
  const [form, setForm] = useState<FormData>(initial);

  /* ---- Fetch products (only those with BOMs) + build BOM map ---- */
  useEffect(() => {
    setLoading(true);setLoadError(false);setError(null);
    Promise.all([
      loadChoices('/manufacturing/boms?isActive=true','boms'),
      loadChoices('/products','products'),
    ]).then(([bomsRes, productsRes]) => {
      const bomsPayload: any = bomsRes.data.data;
      const allBoms: BOM[] = Array.isArray(bomsPayload)
        ? bomsPayload
        : bomsPayload?.boms ?? bomsPayload?.data ?? [];

      // Build productId → BOM map (one BOM per product, take first active)
      const map: Record<string, BOM> = {};
      for (const bom of allBoms) {
        if (!map[bom.productId]) {
          map[bom.productId] = bom;
        }
      }
      setBomMap(map);

      const prodsPayload: any = productsRes.data.data;
      const allProducts: Product[] = Array.isArray(prodsPayload)
        ? prodsPayload
        : prodsPayload?.products ?? prodsPayload?.data ?? [];
      const safeProducts = Array.isArray(allProducts) ? allProducts : [];

      // Only show products that have a BOM
      const filtered = safeProducts.filter((p) => map[p.id]);
      setProducts(filtered);
    }).catch(()=>{setLoadError(true);setProducts([]);setError('Could not load products and BOMs. Try again.');}).finally(() => setLoading(false));

    if(profileManager||isEdit)return;
    api.get('/sales-orders?limit=100')
      .then(res => {
        const list = res.data.data?.salesOrders ?? res.data.data ?? [];
        setSalesOrders(list);
      })
      .catch(() => {});

    // Fetch Manufacturing department employees only
    api.get('/departments')
      .then(deptRes => {
        const depts = deptRes.data.data ?? [];
        const mfgDept = depts.find((d: any) => d.name === 'Manufacturing' || d.name === 'Production');
        const deptFilter = mfgDept ? `&departmentId=${mfgDept.id}` : '';
        return api.get(`/employees?limit=100${deptFilter}`);
      })
      .then(res => {
        const list = res.data.data?.employees ?? res.data.data ?? [];
        setUsers(list);
      })
      .catch(() => {});
  }, [profileManager,isEdit,revision]);

  /* ---- Auto-select BOM when product changes ---- */
  useEffect(() => {
    if (!form.productId) {
      setForm(prev => ({ ...prev, bomId: '' }));
      return;
    }
    // Editing: the order keeps its own BOM until the product is changed, then the new product's BOM is picked as on create.
    if (editOrder && form.productId === editOrder.productId) {
      setForm(prev => ({ ...prev, bomId: editOrder.bomId }));
      return;
    }
    const bom = bomMap[form.productId];
    if (bom) {
      setForm(prev => ({ ...prev, bomId: bom.id }));
    } else {
      setForm(prev => ({ ...prev, bomId: '' }));
    }
  }, [form.productId, bomMap, editOrder]);

  /* ---- Field update helper ---- */
  const updateField = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  /* ---- Validation ---- */
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.productId) errors.productId = 'Product is required';
    if (!form.bomId) errors.bomId = 'No BOM found for selected product';
    const qty = Number(form.targetQty);
    if (!form.targetQty || isNaN(qty) || qty <= 0) {
      errors.targetQty = 'Target quantity must be a positive number';
    }
    if (form.plannedStart && form.plannedEnd && form.plannedStart > form.plannedEnd) {
      errors.plannedEnd = 'End date cannot be before start date';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---- Submit ---- */
  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      if (editOrder) {
        // Send only what changed; the server decides whether the order may still be changed.
        const patch: Record<string, any> = {};
        if (Number(form.targetQty) !== Number(editOrder.targetQty)) patch.targetQty = Number(form.targetQty);
        if (form.productId !== editOrder.productId) { patch.productId = form.productId; patch.bomId = form.bomId; }
        if (form.priority !== editOrder.priority) patch.priority = form.priority;
        if (form.plannedStart !== dateOnly(editOrder.plannedStart)) patch.plannedStart = form.plannedStart || null;
        if (form.plannedEnd !== dateOnly(editOrder.plannedEnd)) patch.plannedEnd = form.plannedEnd || null;
        if (form.notes.trim() !== (editOrder.notes ?? '').trim()) patch.notes = form.notes.trim() || null;
        if (Object.keys(patch).length) await api.put(`/manufacturing/production-orders/${editOrder.id}`, patch);
        if (onCreated) onCreated();
        return;
      }
      const payload: Record<string, any> = {
        productId: form.productId,
        bomId: form.bomId,
        targetQty: Number(form.targetQty),
        priority: form.priority,
      };
      if (form.plannedStart) payload.plannedStart = form.plannedStart;
      if (form.plannedEnd) payload.plannedEnd = form.plannedEnd;
      if (form.salesOrderId) payload.salesOrderId = form.salesOrderId;
      if (form.assignedTo) payload.assignedTo = form.assignedTo;
      if (form.notes.trim()) payload.notes = form.notes.trim();

      const res = await api.post('/manufacturing/production-orders', payload);
      const id = res.data.data?.id ?? res.data.id;
      if(onCreated){onCreated();return;}
      navigate(id ? `/production-orders/${id}` : '/manufacturing/dashboard');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        (isEdit ? 'Failed to save changes' : 'Failed to create production order'),
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---- Selected BOM info ---- */
  const selectedBom = form.productId ? bomMap[form.productId] : null;

  /* ---- Shared input classes ---- */
  const inputCls =
    'w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none transition-colors';
  const inputErrCls =
    'w-full rounded-lg border border-red-500 bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-red-400 focus:outline-none transition-colors';
  const labelCls = 'mb-1 block text-xs font-medium text-[var(--text-secondary)]';

  // An edited order may have lost its BOM from the active list; keep its product visible in the picker.
  const shownProducts = editOrder && !products.some(p => p.id === editOrder.productId) ? [{ id: editOrder.productId, name: editOrder.productName ?? editOrder.number }, ...products] : products;
  const shownBom = selectedBom ?? (editOrder && form.productId === editOrder.productId ? { id: editOrder.bomId, name: 'Current Bill of Materials', productId: editOrder.productId } : null);

  return <ProductionOrderFormView {...{navigate,products:shownProducts,salesOrders,users,form,fieldErrors,selectedBom:shownBom,inputCls,inputErrCls,labelCls,updateField,handleSubmit,saving,error,loading,loadError}} profileManager={profileManager||isEdit} title={editOrder?`Edit ${editOrder.number}`:'New production order'} submitLabel={editOrder?'Save changes':'Create production order'} productLocked={!!editOrder?.productLocked} onCancel={onCancel} retry={()=>setRevision(n=>n+1)}/>;
}
